/**
 * Wiring the audio layer to the cue stream (DESIGN-client §8).
 *
 * `SfxDirector` and `Announcer` were both written, unit-tested and never constructed: nothing in
 * the app attached either one, so every cue-driven sound in the game was silent — card places,
 * trick sweeps, slams, the SET impact, the Boston jingle — and no announcer bark ever reached a
 * subtitle. The pacing audit caught it by asking, for each cue on a real match, whether any sample
 * was requested within 100ms of it. Forty-four in a single hand were not.
 *
 * Two more halves of the same failure are fixed here rather than in the director: the sprite those
 * samples live in was never loaded (`sfxSprite.ts`), and the player's volume sliders never reached
 * the engine outside `?e2e=1`, because `shell/sheets/Settings.tsx` pushes them through
 * `window.__spades.audio`, which only the test hook installs.
 *
 * Both attach to the queue's synchronous `start` listeners so a sound leaves at the same moment
 * the animation does, rather than after the handler's `await`.
 *
 * Nothing here imports the store: settings arrive as thunks through `AudioInstallOptions`, the
 * same seam `TablePorts` uses, so `audio/**` stays testable from a script.
 */
import { getAudioEngine, WEBM_MIME, type AudioEngine, type VolumeTarget } from './AudioEngine';
import {
  MusicDirector,
  createMediaSource,
  type MusicFormat,
  type MusicSnapshot,
  type MusicSource,
  type MusicTrack,
} from './MusicDirector';
import { assetBase } from '../scene/assets/base';
import { Announcer, type Subtitle } from './Announcer';
import { SfxDirector, type CueStartSource } from './SfxDirector';
import { loadSfxSprite } from './sfxSprite';
import { installAudioTestApi, installEngineTestApi, installMusicTestApi } from './testHooks';

/** `settings.volume` plus the master level, exactly as the store holds them (0..1). */
export type VolumeLevels = Partial<Record<VolumeTarget, number>>;

/** Everything the app tells the audio layer about itself. All optional. */
export interface AudioSettingsPorts {
  /** Where an announcer bark's subtitle goes (`presentation.subtitle`). */
  setSubtitle?: (s: Subtitle) => void;
  /** `settings.subtitles`; barks stay audible when it is off, they just do not caption. */
  subtitlesEnabled?: () => boolean;
  /** `settings.volume` + `master`. Read at install and again on every `subscribeVolumes` fire. */
  volumes?: () => VolumeLevels;
  /** Notifies when `settings.volume` changes. Returns an unsubscribe. */
  subscribeVolumes?: (cb: () => void) => () => void;
  /**
   * Should this cue be silent? Used for `emote`, whose mute is keyed on the *sender*, not the emote.
   *
   * The predicate rather than the list, because resolving "player X is muted" to "seat N is muted"
   * needs the public view, which `audio/**` has no business holding. `TableScene.isEmoteMuted` owns
   * that lookup and both the sticker and the pop go through it.
   */
  muteSeat?: (seat: number) => boolean;
}

export interface AudioInstallOptions extends AudioSettingsPorts {
  /** The app's cue queue (structural: `SfxDirector` and `Announcer` both take this shape). */
  cueQueue: CueStartSource;
  /** Skip the sprite fetch (unit tests, screenshot runs that register a silent sprite instead). */
  loadSprite?: boolean;
}

export interface AudioInstall {
  director: SfxDirector;
  announcer: Announcer;
  /** Resolves once the sprite is registered (or has failed); `true` when samples are available. */
  ready: Promise<boolean>;
  dispose(): void;
}

let current: AudioInstall | null = null;

/**
 * Attach the sfx director and the announcer to `cueQueue`. Idempotent: a second call replaces the
 * first, so React's StrictMode double-mount and a table rebuild are both harmless.
 */
export function installAudio(opts: AudioInstallOptions): AudioInstall {
  current?.dispose();
  const engine = getAudioEngine();

  // Nothing in the shipped app ever unlocked the engine. `shell/screens/Boot.tsx` asks for it
  // through `audioHooks()?.tryUnlock?.()`, and `audioHooks()` reads `window.__spades.audio`, which
  // only `installAudioTestApi` creates and only under `?e2e=1` — so in production `unlocked` stayed
  // false for the whole session and every `play()` reported `audible:false` and returned null. By
  // the time a table exists the player has clicked at least twice, so `resume()` is allowed here;
  // `ensureAutoResume` (wired by this call) then covers tab-switches and a policy that says no.
  engine.tryUnlock();
  // The `?e2e=1` probes follow the install rather than each dev page, so the root path has them too.
  installAudioTestApi(engine);

  const applyVolumes = (): void => {
    const levels = opts.volumes?.();
    if (!levels) return;
    for (const [which, v] of Object.entries(levels)) {
      if (typeof v === 'number') engine.setVolume(which as VolumeTarget, v);
    }
  };
  applyVolumes();
  const offVolumes = opts.subscribeVolumes?.(applyVolumes) ?? (() => undefined);

  const director = new SfxDirector(engine, {
    // A muted player's emote draws no sticker; it must not pop either.
    skip: (cue) => {
      if (cue.type !== 'emote' || !opts.muteSeat) return false;
      const seat = (cue.data as { seat?: number }).seat;
      return typeof seat === 'number' && opts.muteSeat(seat);
    },
  });
  const announcer = new Announcer(engine, {
    setSubtitle: opts.setSubtitle ?? (() => undefined),
    ...(opts.subtitlesEnabled ? { subtitlesEnabled: opts.subtitlesEnabled } : {}),
  });
  const offDirector = director.attach(opts.cueQueue);
  const offAnnouncer = announcer.attach(opts.cueQueue);
  const ready = opts.loadSprite === false ? Promise.resolve(false) : loadSfxSprite();

  const install: AudioInstall = {
    director,
    announcer,
    ready,
    dispose() {
      offDirector();
      offAnnouncer();
      offVolumes();
      if (current === install) current = null;
    },
  };
  current = install;
  return install;
}

export function getAudioInstall(): AudioInstall | null {
  return current;
}

/* ------------------------------------------------------- first-gesture unlock */

const GESTURES = ['pointerdown', 'touchstart', 'keydown'] as const;

let firstGestureOff: (() => void) | null = null;

/**
 * Unlock the audio engine on the first user gesture anywhere in the app.
 *
 * Audio used to be unlocked by the Boot screen's "Deal me in" tap — one tap that also asked for
 * hardware acceleration and the orientation lock. Boot now continues by itself the moment loading
 * finishes and identity resolves, so that tap is gone, and with it the only gesture the engine was
 * ever offered on the menu: `installAudio` is built by the table scene, so nothing on the boot or
 * lobby screens called `unlock` at all. The lobby music bed would have stayed silent until the
 * first hand.
 *
 * A gesture is a gesture whatever it lands on, so this listens on the document instead: the first
 * click on a menu tile, the first key, the first touch. Capture phase and passive, so it cannot
 * change what the click does or delay it, and it detaches itself once the engine reports unlocked.
 * It is not `{ once: true }` for the same reason `ensureAutoResume` is not — a policy may refuse
 * the first gesture, and a session that stays silent because we stopped asking is the worst
 * outcome.
 *
 * Idempotent: a second call replaces the first, so StrictMode's double effect is harmless.
 */
export function installFirstGestureUnlock(engineArg?: AudioEngine): () => void {
  firstGestureOff?.();
  if (typeof document === 'undefined') return () => undefined;
  const engine = engineArg ?? getAudioEngine();

  const off = (): void => {
    for (const type of GESTURES) document.removeEventListener(type, onGesture, true);
    if (firstGestureOff === off) firstGestureOff = null;
  };
  const onGesture = (): void => {
    engine.unlock();
    if (engine.isUnlocked) off();
  };

  for (const type of GESTURES) document.addEventListener(type, onGesture, { capture: true, passive: true });
  firstGestureOff = off;

  // The gesture may already have happened: a Discord launch click, or a click on the page that
  // opened before this ran. `tryUnlock` is the no-gesture form and costs nothing when it fails.
  engine.tryUnlock();
  if (engine.isUnlocked) off();
  return off;
}

// --- music agent -------------------------------------------------------------------------------
/**
 * Wiring the music beds to the store (`docs/AUDIO-MUSIC.md`).
 *
 * Separate from `installAudio` and on a different clock, because they have different lifetimes.
 * `installAudio` is built by the table scene and dies with it; the music starts on the boot screen
 * and outlives every table in the session, so it is installed once from `main.tsx` instead.
 *
 * Still no store import: the state arrives through `state()` / `subscribeState()` exactly the way
 * the volumes do above, so this file remains loadable from a unit test.
 */

/** The slice of the store the music mapping reads. Structural: `RootState` satisfies it. */
export interface MusicStoreShape {
  session: { platform: 'web' | 'discord' };
  presentation: { screen: string; layoutMode: string };
  table: { phase: string; handNo: number; suddenDeath: number; teams: Array<{ score: number }>; rulesJson: string } | null;
  settings: { volume: VolumeLevels & { music: number }; audioMuted?: boolean };
}

/**
 * `table.rulesJson` → the `format` the bed choice needs, or null when there is nothing to read.
 *
 * Hand-parsed rather than run through `resolveRules`, which would pull zod and the whole shared
 * rules schema into a module the boot path loads. A malformed value is null, which means "assume
 * no hand is the last one" — the wrong bed for one hand, never a thrown boot.
 */
export function musicFormatFromRules(rulesJson: string | undefined): MusicFormat | null {
  if (!rulesJson) return null;
  try {
    const f = (JSON.parse(rulesJson) as { format?: { kind?: string; hands?: number; target?: number } }).format;
    if (!f) return null;
    if (f.kind === 'money') return { kind: 'money' };
    if (f.kind === 'quick') return { kind: 'quick', hands: typeof f.hands === 'number' ? f.hands : 4 };
    if (f.kind === 'classic' && typeof f.target === 'number') return { kind: 'classic', target: f.target };
    return null;
  } catch {
    return null;
  }
}

/** One store read plus the tab's visibility, in the shape `MusicMachine` wants. */
export function musicSnapshot(s: MusicStoreShape, hidden: boolean): MusicSnapshot {
  const t = s.table;
  return {
    screen: s.presentation.screen,
    phase: t?.phase ?? null,
    handNo: t?.handNo ?? 0,
    suddenDeath: t?.suddenDeath ?? 0,
    scores: [t?.teams?.[0]?.score ?? 0, t?.teams?.[1]?.score ?? 0],
    format: musicFormatFromRules(t?.rulesJson),
    layoutMode: s.presentation.layoutMode,
    hidden,
    volume: s.settings.volume.music,
  };
}

export interface MusicInstallOptions {
  state: () => MusicStoreShape;
  /** Any store change; the director is cheap enough to re-plan on all of them. */
  subscribeState: (cb: () => void) => () => void;
  /**
   * Set `settings.volume.music`. Used exactly once, to drop a first-time Discord player to the
   * quieter surface default; never called when a level was rehydrated from a previous session.
   */
  setMusicVolume?: (v: number) => void;
  /** Did storage carry a music level the player had already chosen? (`hasPersistedMusicVolume`) */
  hasStoredVolume?: () => boolean;
  /** Per-surface first-run level, `store.DEFAULT_MUSIC_VOLUME`. */
  platformDefaults?: Record<'web' | 'discord', number>;
  /** Test seams. */
  engine?: AudioEngine;
  createSource?: (track: MusicTrack) => MusicSource | null;
  base?: string;
  canPlayWebm?: boolean;
  now?: () => number;
}

export interface MusicInstall {
  director: MusicDirector;
  dispose(): void;
}

let currentMusic: MusicInstall | null = null;

/**
 * Start the music beds. Idempotent: a second call replaces the first, so StrictMode's double
 * effect and an HMR reload are both harmless.
 */
export function installMusic(opts: MusicInstallOptions): MusicInstall {
  currentMusic?.dispose();
  const engine = opts.engine ?? getAudioEngine();

  // The surface default, applied at most once and only to a player who has never touched the
  // slider. `session.platform` is set asynchronously by `bridge/boot.ts`, so this is checked on
  // store changes rather than at install time.
  let platformDefaultApplied = opts.hasStoredVolume?.() ?? true;
  const applyPlatformDefault = (): void => {
    if (platformDefaultApplied || !opts.setMusicVolume || !opts.platformDefaults) return;
    const platform = opts.state().session.platform;
    if (platform !== 'discord') return;
    platformDefaultApplied = true;
    opts.setMusicVolume(opts.platformDefaults.discord);
  };
  applyPlatformDefault();

  // The volume sliders, before there is a table to install them.
  //
  // `installAudio` pushes `settings.volume` into the engine, but the table scene builds it, so on
  // the boot and lobby screens nothing did — and those are exactly the screens the lobby bed plays
  // on. Without this, moving the music (or master) slider in the menu wrote the store and changed
  // nothing anybody could hear. Applying it from both places is harmless: `setVolume` is a clamped
  // 80 ms ramp to an absolute value, so the two never fight.
  const applyVolumes = (): void => {
    const settings = opts.state().settings;
    for (const [which, v] of Object.entries(settings.volume)) {
      if (typeof v === 'number') engine.setVolume(which as VolumeTarget, v);
    }
    // The header's speaker button. Applied here, not in `installAudio`, for the same reason as the
    // sliders: this install is the one alive on every screen.
    engine.setMuted(settings.audioMuted === true);
  };
  applyVolumes();

  const director = new MusicDirector({
    engine,
    snapshot: () => musicSnapshot(opts.state(), typeof document !== 'undefined' && document.hidden),
    subscribe: (cb) => {
      const listener = (): void => {
        applyPlatformDefault();
        applyVolumes();
        cb();
      };
      const offs = [opts.subscribeState(listener)];
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', listener);
        window.addEventListener('focus', listener);
        window.addEventListener('pageshow', listener);
        offs.push(() => {
          document.removeEventListener('visibilitychange', listener);
          window.removeEventListener('focus', listener);
          window.removeEventListener('pageshow', listener);
        });
      }
      return () => {
        for (const off of offs) off();
      };
    },
    createSource:
      opts.createSource ??
      ((track) =>
        createMediaSource(track, {
          engine,
          base: opts.base ?? assetBase(),
          canPlayWebm: opts.canPlayWebm ?? engine.canPlayMime(WEBM_MIME),
        })),
    ...(opts.now ? { now: opts.now } : {}),
  });

  // The ENGINE probes, from the boot screen rather than from the first table: a spec standing on
  // the menu had no `isUnlocked` to poll, and the menu is exactly where the lobby bed has to be
  // proved.
  //
  // Deliberately not `installAudioTestApi`. That one also publishes the announcer probes, and
  // `announcer.spec.ts` polls `clipLoaded` as its gate for "the announcer is attached" — which is
  // only true once `installAudio` has run. Publishing them here made that gate go green while
  // nothing was listening for barks, and the spec's first bark was dropped.
  installEngineTestApi(engine);
  installMusicTestApi(director);
  director.start();
  const install: MusicInstall = {
    director,
    dispose() {
      director.dispose();
      if (currentMusic === install) currentMusic = null;
    },
  };
  currentMusic = install;
  return install;
}

export function getMusicInstall(): MusicInstall | null {
  return currentMusic;
}
// --- end music agent ---------------------------------------------------------------------------
